import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  defaultLandingForRole,
  isPathAllowedForRole,
  useAuthStore,
  type Role,
} from '../../store/useAuthStore';
import { isPathInExtraAccess } from '../../lib/accessSurfaces';

interface ProtectedRouteProps {
  /** Legacy alias for `allowedRoles={['ADMIN']}`. */
  requireAdmin?: boolean;
  /** Whitelist of roles permitted to view the route. Default: any signed-in role. */
  allowedRoles?: Role[];
}

export default function ProtectedRoute({
  requireAdmin = false,
  allowedRoles,
}: ProtectedRouteProps) {
  const { user, role, profile, extraAccess, authInitialized } = useAuthStore();
  const location = useLocation();

  if (!authInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-secondary font-bold tracking-widest uppercase text-sm">
          Verifying access...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/staff/login" state={{ from: location }} replace />;
  }

  // Onboarding firewall — back-office roles are exempted. Measurers don't
  // need a full profile before doing field work; finance, finance manager,
  // and purchaser never appear on a customer-facing artifact (name/whatsapp
  // surface only in quotes and notifications, none of which they author). An
  // admin can fill in their details later if needed.
  const isProfileIncomplete = !profile?.fullName || !profile?.whatsapp;
  const exemptFromOnboarding =
    role === 'MEASURER' ||
    role === 'FINANCE' ||
    role === 'FINANCE_MANAGER' ||
    role === 'PURCHASER' ||
    role === 'SALES_MANAGER';
  if (isProfileIncomplete && !exemptFromOnboarding && location.pathname !== '/staff/onboard') {
    return <Navigate to="/staff/onboard" replace />;
  }

  if (!isProfileIncomplete && location.pathname === '/staff/onboard') {
    return <Navigate to={defaultLandingForRole(role)} replace />;
  }

  // Per-route role whitelist, with the per-user `extraAccess` override as a
  // second-chance gate. Admin grants override prefixes on the Staff Manager;
  // ADR 0005 §3 covers the model (code defaults + per-user overrides). The
  // override is a UI/route gate only — Firestore rules and Cloud Function
  // role checks stay role-only, so a granted prefix can't escalate writes
  // beyond what the role itself permits server-side.
  const effectiveAllowed = allowedRoles ?? (requireAdmin ? (['ADMIN'] as Role[]) : null);
  if (
    effectiveAllowed &&
    !effectiveAllowed.includes(role) &&
    !isPathInExtraAccess(extraAccess, location.pathname)
  ) {
    return <Navigate to={defaultLandingForRole(role)} replace />;
  }

  // Path-fenced roles (Measurer, Finance, Finance Manager, Purchaser, and
  // baseline STAFF post-Phase-0b) are restricted to their subset of paths
  // regardless of any explicit allowedRoles. This catches direct URL access
  // and stale links so a typed-in /staff/admin won't render an admin shell
  // for them. `extraAccess` again opens a second-chance gate for admin-
  // granted overrides.
  if (
    (role === 'MEASURER' ||
      role === 'FINANCE' ||
      role === 'FINANCE_MANAGER' ||
      role === 'PURCHASER' ||
      role === 'STAFF' ||
      role === 'SALES_MANAGER') &&
    !isPathAllowedForRole(role, location.pathname) &&
    !isPathInExtraAccess(extraAccess, location.pathname)
  ) {
    return <Navigate to={defaultLandingForRole(role)} replace />;
  }

  return <Outlet />;
}
